import { inject, Injectable } from '@angular/core';
import {
  SyncWorkflowFormResponse,
  WorkflowFormSubmissionExternal,
  WorkflowFormValidationError,
} from '@workflows/types';
import {
  BehaviorSubject,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { XAlertService } from '@core/services';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { WorkflowsTranslations } from '@core/types';
import { WorkflowDataService } from './workflow-data.service';
import { WorkflowErrorsService } from './workflow-errors.service';
import { WorkflowHttpErrorsService } from './workflow-http-errors.service';
import { WorkflowOfflineService } from './workflow-offline.service';
import { WorkflowSelectorService } from './workflow-selector.service';
import { WorkflowStateService } from './workflow-state.service';
import { workflowInstanceKeyToString } from './storage';
import {
  classifyWorkflowError,
  classifyWorkflowResponse,
} from './workflow-sync-result';

export interface WorkflowBackgroundSyncTask {
  requestNumber: string;
  workflowId: string;
  nodeId: number;
  formId: number;
  body: {
    submission?: {
      id?: number;
      data?: WorkflowFormSubmissionExternal['data'];
    };
    nextFormId?: number;
  };
  isFormValid: boolean;
}

/**
 * Фоновая синхронизация формы регламента.
 *
 * Сохранения одного экземпляра формы (requestWorkflowId, nodeId, formId) выполняются
 * строго последовательно: следующий запрос уходит только после ответа на предыдущий,
 * поэтому более старые данные не могут перезаписать новые на сервере.
 * Если по экземпляру уже есть задачи в офлайн-очереди, новое сохранение встаёт за ними.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkflowBackgroundSyncService {
  private readonly stateService = inject(WorkflowStateService);
  private readonly selectorService = inject(WorkflowSelectorService);
  private readonly errorsService = inject(WorkflowErrorsService);
  private readonly workflowHttpErrorsService = inject(
    WorkflowHttpErrorsService,
  );
  private readonly dataService = inject(WorkflowDataService);
  private readonly workflowOfflineService = inject(WorkflowOfflineService);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);

  private pendingByNode = new Map<number, number>();
  private readonly pendingByNode$ = new BehaviorSubject<Map<number, number>>(
    new Map<number, number>(),
  );
  /** Последняя операция в цепочке экземпляра формы */
  private readonly chainByInstance = new Map<string, Promise<void>>();

  /**
   * Счетчик фоновых сабмитов по ноде для UI (stepper loader)
   */
  getPendingCountForNode$(nodeId: number): Observable<number> {
    return this.pendingByNode$.pipe(
      map(pendingMap => pendingMap.get(nodeId) ?? 0),
      distinctUntilChanged(),
    );
  }

  /**
   * Запустить фоновую синхронизацию формы.
   * Не возвращает Promise, т.к. задача работает в фоне после навигации.
   */
  syncFormInBackground(task: WorkflowBackgroundSyncTask): void {
    const instanceKey = this.getInstanceKey(task);
    const operationId = crypto.randomUUID();
    const previous = this.chainByInstance.get(instanceKey) ?? Promise.resolve();

    this.incrementPending(task.nodeId);

    const current = previous
      .then(() => this.executeTask(task, instanceKey, operationId))
      .catch(error => console.error('Background form sync failed:', error))
      .finally(() => {
        this.decrementPending(task.nodeId);

        if (this.chainByInstance.get(instanceKey) === current) {
          this.chainByInstance.delete(instanceKey);
        }
      });

    this.chainByInstance.set(instanceKey, current);
  }

  /**
   * Сохранить форму без сети: задача записывается на устройство (durable) и только после
   * этого форма помечается как ожидающая отправки. Ошибка записи пробрасывается.
   */
  async queueFormOffline(task: WorkflowBackgroundSyncTask): Promise<void> {
    await this.workflowOfflineService.addSubmissionToQueue({
      requestNumber: task.requestNumber,
      workflowId: task.workflowId,
      nodeId: task.nodeId,
      formId: task.formId,
      body: task.body,
      timestamp: Date.now(),
    });

    this.updateSubmissionPending(task);
  }

  private async executeTask(
    task: WorkflowBackgroundSyncTask,
    instanceKey: string,
    operationId: string,
  ): Promise<void> {
    // Более ранние офлайн-задачи этого экземпляра ещё не отправлены — не обгоняем их
    if (
      await this.workflowOfflineService.hasQueuedSubmissionsForInstance(
        instanceKey,
      )
    ) {
      await this.queueWithUserNotice(task, operationId);

      return;
    }

    let syncResponse: SyncWorkflowFormResponse;

    try {
      syncResponse = await firstValueFrom(
        this.stateService.syncWorkflow(
          task.requestNumber,
          task.workflowId,
          task.nodeId,
          task.formId,
          task.body,
          operationId,
        ),
      );
    } catch (error) {
      await this.handleSyncError(task, operationId, error);

      return;
    }

    await this.handleSyncResponse(task, syncResponse);
  }

  private async handleSyncResponse(
    task: WorkflowBackgroundSyncTask,
    syncResponse: SyncWorkflowFormResponse,
  ): Promise<void> {
    const classification = classifyWorkflowResponse(syncResponse);

    // Валидационные ошибки backend: данные сохранены, но форма невалидна
    if (classification.kind === 'validation-failed') {
      const validationErrors = classification.error?.validationErrors ?? [];

      if (validationErrors.length > 0) {
        await this.saveFormErrorsSafe(task, validationErrors);
      }

      this.updateSubmissionAfterSync(task, false);
      this.workflowHttpErrorsService.showSyncValidationErrors(
        validationErrors as WorkflowFormValidationError[],
      );

      return;
    }

    if (classification.kind !== 'success') {
      this.xAlertService.error(
        classification.error?.message ?? 'Form sync error',
      );

      return;
    }

    // Успех: очищаем сохраненные backend-ошибки и драфт; валидность подтверждена сервером
    await this.errorsService.removeFormErrors(
      task.requestNumber,
      task.workflowId,
      task.nodeId,
      task.formId,
    );

    await this.dataService.removeFormDraft(
      task.requestNumber,
      task.workflowId,
      task.nodeId,
      task.formId,
    );

    this.updateSubmissionAfterSync(task, true);
  }

  private async handleSyncError(
    task: WorkflowBackgroundSyncTask,
    operationId: string,
    error: unknown,
  ): Promise<void> {
    const classification = classifyWorkflowError(error);

    // Временная ошибка (нет сети, 5xx...): задача уходит в офлайн-очередь с тем же operationId —
    // если сервер всё-таки выполнил запрос, повтор вернёт сохранённый результат
    if (classification.kind === 'retryable') {
      await this.queueWithUserNotice(task, operationId);

      return;
    }

    this.workflowHttpErrorsService.showSyncHttpError(
      error as HttpErrorResponse,
    );
    console.error('Form sync error:', error);
  }

  private async queueWithUserNotice(
    task: WorkflowBackgroundSyncTask,
    operationId: string,
  ): Promise<void> {
    try {
      await this.workflowOfflineService.addSubmissionToQueue({
        requestNumber: task.requestNumber,
        workflowId: task.workflowId,
        nodeId: task.nodeId,
        formId: task.formId,
        body: task.body,
        timestamp: Date.now(),
        operationId,
      });
    } catch (storageError) {
      // Задача не сохранилась на устройстве — не обещаем отправку
      console.error('Failed to queue offline form submission:', storageError);
      this.xAlertService.error(
        this.outkeepTranslateService.instant(
          WorkflowsTranslations.OfflineFormNotSaved,
        ),
      );

      return;
    }

    this.updateSubmissionPending(task);

    this.xAlertService.warn(
      this.outkeepTranslateService.instant(
        WorkflowsTranslations.OfflineFormQueued,
      ),
    );
  }

  private async saveFormErrorsSafe(
    task: WorkflowBackgroundSyncTask,
    validationErrors: WorkflowFormValidationError[],
  ): Promise<void> {
    try {
      await this.errorsService.saveFormErrors(
        task.requestNumber,
        task.workflowId,
        task.nodeId,
        task.formId,
        validationErrors,
      );
    } catch (error) {
      // Ошибки всё равно показаны пользователю; на устройстве они не сохранились
      console.error('Failed to save form validation errors:', error);
    }
  }

  private updateSubmissionAfterSync(
    task: WorkflowBackgroundSyncTask,
    isValid: boolean,
  ): void {
    if (this.selectorService.areWorkflowsCompleted()) {
      return;
    }

    this.stateService.updateFormSubmission(
      task.workflowId,
      task.nodeId,
      task.formId,
      task.body.submission?.data,
      isValid,
      false, // не трогаем lastVisitedFormId при фоне
    );
  }

  /**
   * Форма сохранена офлайн: ждёт отправки и не считается валидной сервером,
   * клиентская валидность хранится отдельно (pendingSync + clientValid)
   */
  private updateSubmissionPending(task: WorkflowBackgroundSyncTask): void {
    if (this.selectorService.areWorkflowsCompleted()) {
      return;
    }

    this.stateService.updateFormSubmission(
      task.workflowId,
      task.nodeId,
      task.formId,
      task.body.submission?.data,
      false,
      false, // не трогаем lastVisitedFormId при фоне
      { pendingSync: true, clientValid: task.isFormValid },
    );
  }

  private getInstanceKey(task: WorkflowBackgroundSyncTask): string {
    return workflowInstanceKeyToString(task.requestNumber, {
      requestWorkflowId: task.workflowId,
      nodeId: task.nodeId,
      formId: task.formId,
    });
  }

  private incrementPending(nodeId: number): void {
    const current = this.pendingByNode.get(nodeId) ?? 0;

    this.pendingByNode.set(nodeId, current + 1);
    this.pendingByNode$.next(new Map(this.pendingByNode));
  }

  private decrementPending(nodeId: number): void {
    const current = this.pendingByNode.get(nodeId) ?? 0;

    if (current <= 1) {
      this.pendingByNode.delete(nodeId);
    } else {
      this.pendingByNode.set(nodeId, current - 1);
    }
    this.pendingByNode$.next(new Map(this.pendingByNode));
  }
}
